import { lazy, Suspense, useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { HelmetProvider } from "@/lib/helmet";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageWrapper } from "@/components/LanguageWrapper";
import { CookiebotInitializer } from "@/components/CookiebotInitializer";
import { GlobalRedirectHandler } from "@/components/GlobalRedirectHandler";
import { ScrollToTop } from "@/components/ScrollToTop";
import { DeferredGTM } from "@/components/DeferredGTM";
import { AIAssistantProvider, useAIAssistant } from "@/contexts/AIAssistantContext";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import NotFound from "@/pages/NotFound";
import { siteSettingsQueryOptions } from "@/hooks/useSiteSettings";
import { currentYear } from "@/lib/currentYear";

// NOTE: computed per request — a module-scope year evaluates to 1970 in the
// edge runtime, where the clock reads 0 during module evaluation.
const siteTitle = () =>
  `Kreditkarten.de - Kreditkartenvergleich ${currentYear()} – Beste & kostenlose Kreditkarten in Deutschland`;
const siteDescription = () =>
  `Vergleichen Sie jetzt die beliebtesten Kreditkarten Deutschlands! Kostenlose Kreditkarten ohne Jahresgebühr, Reisekreditkarten mit Versicherungen und Cashback-Angebote im Vergleich – finden Sie die beste Kreditkarte ${currentYear()}.`;
const AIAssistantDialog = lazy(() =>
  import("@/components/AIAssistantDialog").then((m) => ({ default: m.AIAssistantDialog })),
);

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Preload site settings during SSR and dehydrate to the client so
  // header/logo/language render identically on both sides (no hydration race).
  loader: ({ context }) => context.queryClient.ensureQueryData(siteSettingsQueryOptions).catch(() => null),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: siteTitle() },
      { name: "description", content: siteDescription() },
      { name: "author", content: "Kreditkarten.de" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: siteTitle() },
      { property: "og:description", content: siteDescription() },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@lovable_dev" },
      { name: "twitter:title", content: siteTitle() },
      { name: "twitter:description", content: siteDescription() },
      { name: "msvalidate.01", content: "B4F37EF33C1308B3FBFCB4742DF8DBD8" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://qrbdfmixhqdsoafueeuq.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://qrbdfmixhqdsoafueeuq.supabase.co" },
      // Card images are served from the partner image host — open the
      // connection early so the largest image isn't waiting on a handshake.
      { rel: "preconnect", href: "https://bilder.financeads.net", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://bilder.financeads.net" },
      { rel: "dns-prefetch", href: "https://consent.cookiebot.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      {
        rel: "preload",
        href: "/fonts/inter-latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="de" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) — ported from index.html */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-WR46MP54"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// Conditional AI Assistant - only loads chunk when dialog is opened
function ConditionalAssistant() {
  const { showAIDialog } = useAIAssistant();
  return <Suspense fallback={null}>{showAIDialog && <AIAssistantDialog />}</Suspense>;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageWrapper>
          <CookiebotInitializer />
          <TooltipProvider>
            <AIAssistantProvider>
              <Suspense fallback={null}>
                <Toaster />
                <Sonner />
              </Suspense>
              <GlobalRedirectHandler />
              <ScrollToTop />
              <DeferredGTM />
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
              <ConditionalAssistant />
            </AIAssistantProvider>
          </TooltipProvider>
        </LanguageWrapper>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold">This page didn&apos;t load</h1>
        <p className="mb-6 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a className="rounded-md border border-border bg-card px-4 py-2" href="/">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
